import {objectFromEntries} from '@benchling/js-utils';
import {useCallback, useMemo, useState} from 'react';
import _ from 'underscore';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {rowIdAccessor} from 'data-frames/util/createAccessors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export interface UseDataFrameTableSelectionOutput {
  selectedRowIds: Set<string>;
  onClickRow: (rowIndex: number, isShiftDown: boolean) => void;
  onClickSelectAll: (startRowIndex: number, endRowIndex: number) => void;
  clearAnchorRow: () => void;
}

const dataFrameTableSelectionState = makeUseGlobalClientState(new Set<string>());

/**
 * Manage the selection state for `DataFrameTable` using global client state
 */
export default function useDataFrameTableSelectionState(
  clientStateId: string,
  dataFrame: DataFrame
): UseDataFrameTableSelectionOutput {
  const [selectedRowIds, setSelectedRowIds] =
    dataFrameTableSelectionState.useGlobalClientState(clientStateId);

  return useControlledDataFrameTableSelectionState(dataFrame, selectedRowIds, setSelectedRowIds);
}

/**
 * Manage the selection state for `DataFrameTable` using the provided state and state mutator
 */
export function useControlledDataFrameTableSelectionState(
  dataFrame: DataFrame,
  selectedRowIds: Set<string>,
  onSelectedRowIdsChange: (selectedRowIds: Set<string>) => void
): UseDataFrameTableSelectionOutput {
  const rowIndexToRowId = useMemo(
    () => objectFromEntries(dataFrame.rows.map((row, index) => [index, rowIdAccessor(row)])),
    [dataFrame.rows]
  );

  // Not using useGlobalClientState since we want to reset the anchor rows between mounts
  const [anchorRowIndex, setAnchorRowIndex] = useState<number | null>(null);

  const toggleRowIds = useCallback(
    (newSelectedRowIds: Array<string>, shouldToggleAnchorRow: boolean) => {
      const newSelectedRowIdsSet = new Set(selectedRowIds);
      // Toggle anchorRowId in the case of shift-selection because it gets pre-maturely toggled
      // when the user clicks on the first row of the selection range
      if (shouldToggleAnchorRow && anchorRowIndex != null) {
        const anchorRowId = rowIndexToRowId[anchorRowIndex];
        if (newSelectedRowIdsSet.has(anchorRowId)) {
          newSelectedRowIdsSet.delete(anchorRowId);
        } else {
          newSelectedRowIdsSet.add(anchorRowId);
        }
      }
      if (newSelectedRowIds.some((rowId) => !newSelectedRowIdsSet.has(rowId))) {
        onSelectedRowIdsChange(new Set([...newSelectedRowIdsSet, ...newSelectedRowIds]));
      } else {
        onSelectedRowIdsChange(
          new Set([...newSelectedRowIdsSet].filter((rowId) => !newSelectedRowIds.includes(rowId)))
        );
      }
    },
    [onSelectedRowIdsChange, selectedRowIds, rowIndexToRowId, anchorRowIndex]
  );

  const getRowIdsInRange = useCallback(
    (startRowIndex: number, endRowIndex: number) =>
      _.range(startRowIndex, endRowIndex + 1).map((rowIndex) => rowIndexToRowId[rowIndex]),
    [rowIndexToRowId]
  );

  const onClickRow = useCallback(
    (rowIndex: number, isShiftDown: boolean) => {
      if (isShiftDown && anchorRowIndex != null) {
        const startRowIndex = Math.min(anchorRowIndex, rowIndex);
        const endRowIndex = Math.max(anchorRowIndex, rowIndex);

        const rowIdsInRange = getRowIdsInRange(startRowIndex, endRowIndex);
        toggleRowIds(rowIdsInRange, true);
      } else {
        toggleRowIds([rowIndexToRowId[rowIndex]], false);
      }
      setAnchorRowIndex(rowIndex);
    },
    [anchorRowIndex, rowIndexToRowId, toggleRowIds, getRowIdsInRange]
  );

  const onClickSelectAll = useCallback(
    (startRowIndex: number, endRowIndex: number) => {
      toggleRowIds(getRowIdsInRange(startRowIndex, endRowIndex), false);
    },
    [getRowIdsInRange, toggleRowIds]
  );

  const clearAnchorRow = useCallback(() => {
    setAnchorRowIndex(null);
  }, []);

  return {
    selectedRowIds,
    onClickRow,
    onClickSelectAll,
    clearAnchorRow,
  };
}
